import { useCallback, useMemo, useState } from 'react'
import { RepoSelectModal, UploadedRepo } from '~/components/repo-selection/LegacyRepoSelectModal'
import { Selector, SelectItem } from '~/components/common/Selector'
import { Typography } from '~/components/common/Typography'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useRepositoryBranches } from '~/hooks/useCodeContext'
import {
  CommandType,
  IntegrationPlatform,
  integrationPlatformFromJSON,
  integrationPlatformToJSON
} from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { ProjectSelectionOption } from '~/store/taskFormSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { getIntegrationPlatformItems } from '~/utils/agentUtils'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import {
  MetadataFileSelectModal,
  UploadedMetadataFile
} from '../task-forms/shared/mule-refactor/MetadataFileSelectModal'
import { SpringIntegrationRefactorFlowLayout } from './SpringIntegrationRefactorFlowLayout'
import { ProgressSpinner } from '~/components/common/ProgressSpinner'

// Purple filled circular checkmark icon for selected state.
// Circle fill is sourced from the design token `tokens.primary.2` (#65558f)
// via `currentColor`. The check stroke needs a different colour from the
// circle (white on purple), so it uses a token-driven Tailwind class
// (`stroke-tokens-neutral-bg-white`) instead of the literal `stroke="white"`
// — the literal would violate the CLAUDE.md "design tokens only" rule and
// would not respond to theme changes.
const SelectedCheckmark = () => (
  <div className='shrink-0 mt-0.5 text-tokens-primary-2'>
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <circle cx='10' cy='10' r='10' fill='currentColor' />
      <path
        d='M6 10L9 13L14 7'
        className='stroke-tokens-neutral-bg-white'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

interface SpringIntegrationRefactorRepoSelectionScreenProps {
  onClose: () => void
  // NOTE: this component does NOT accept a `repositories` prop. The
  // command-scoped repo list is fetched internally via
  // useRepositoryBranches(SPRING_INTEGRATION_REFACTORING) so the dropdowns
  // stay filtered correctly. A prior interface accepted `repositories`
  // but the body never consumed it — silently dropping caller-supplied
  // filtering. Removed to make the public surface match actual behaviour.
  onSave: (payload: {
    legacyRepos: RepositoryBranch[]
    legacyOption: ProjectSelectionOption
    legacyS3Paths: string[]
    mulesoftRepos: RepositoryBranch[]
    mulesoftOption?: ProjectSelectionOption
    mulesoftS3Paths: string[]
    platform: IntegrationPlatform
    metadataS3Paths?: string[]
  }) => void
  /** True while the parent is creating the task; swaps the form for the skeleton. */
  isSubmitting?: boolean
}

export function SpringIntegrationRefactorRepoSelectionScreen({
  onClose,
  onSave,
  isSubmitting = false
}: SpringIntegrationRefactorRepoSelectionScreenProps) {
  const isPlugin = bridge.isInPlugin()
  // Legacy repository state (required)
  const [legacyRepoModalOpen, setLegacyRepoModalOpen] = useState(false)
  const [legacySelectedRepos, setLegacySelectedRepos] = useState<RepositoryBranch[]>([])
  const [legacyUploadedRepos, setLegacyUploadedRepos] = useState<UploadedRepo[]>([])

  // Source (Mule/legacy) repository state (optional)
  const [mulesoftRepoModalOpen, setMulesoftRepoModalOpen] = useState(false)
  const [mulesoftSelectedRepos, setMulesoftSelectedRepos] = useState<RepositoryBranch[]>([])
  const [mulesoftUploadedRepos, setMulesoftUploadedRepos] = useState<UploadedRepo[]>([])

  // Metadata files state (optional)
  const [metadataModalOpen, setMetadataModalOpen] = useState(false)
  const [metadataUploadedFiles, setMetadataUploadedFiles] = useState<UploadedMetadataFile[]>([])

  // Platform selection
  // Default to UNRECOGNIZED so the user MUST explicitly select a source
  // platform — the legacy default of TIBCO let users submit silently with
  // the wrong platform if their source was Mule, SAP PI/PO, IBM MQ, etc.
  // `isPlatformSelected` below gates the proceed button on this guard.
  const [platform, setPlatform] = useState<IntegrationPlatform>(IntegrationPlatform.UNRECOGNIZED)

  const { currentWorkspace } = useWorkspaceContext()
  const command = CommandType.SPRING_INTEGRATION_REFACTORING
  const { repos, getBranches } = useRepositoryBranches(command)

  // Convert repos to the format needed for RepoSelectModal
  const selectableRepos = useMemo(
    () =>
      repos.map(repo => ({
        conn_type: repo.conn_type,
        index_name: repo.index_name,
        label: extractFileDisplayName(`${repo.conn_type}:${repo.index_name}`)
      })),
    [repos]
  )

  // Handle legacy repository modal confirmation
  const handleLegacyRepoConfirm = useCallback(
    (data: { repositoryBranches: RepositoryBranch[]; uploadedRepos: UploadedRepo[] }) => {
      setLegacySelectedRepos(data.repositoryBranches)
      setLegacyUploadedRepos(data.uploadedRepos)
      setLegacyRepoModalOpen(false)
    },
    []
  )

  // Handle source (Mule) repository modal confirmation
  const handleMulesoftRepoConfirm = useCallback(
    (data: { repositoryBranches: RepositoryBranch[]; uploadedRepos: UploadedRepo[] }) => {
      setMulesoftSelectedRepos(data.repositoryBranches)
      setMulesoftUploadedRepos(data.uploadedRepos)
      setMulesoftRepoModalOpen(false)
    },
    []
  )

  // Handle metadata files modal confirmation
  const handleMetadataFilesConfirm = useCallback(
    (data: { uploadedFiles: UploadedMetadataFile[] }) => {
      setMetadataUploadedFiles(data.uploadedFiles)
      setMetadataModalOpen(false)
    },
    []
  )

  const isLegacyRepoSelected = legacySelectedRepos.length > 0 || legacyUploadedRepos.length > 0
  const isMulesoftRepoSelected =
    mulesoftSelectedRepos.length > 0 || mulesoftUploadedRepos.length > 0
  // Reject BOTH ``UNRECOGNIZED`` (-1, protobuf-ts sentinel for an out-of-
  // schema value) AND ``UNKNOW_INTEGRATION_PLATFORM`` (0, the proto-wire
  // default). A stored/serialised value that round-trips through
  // ``integrationPlatformFromJSON`` can resolve to 0 even though the user
  // never explicitly picked a platform; without this both checks an
  // effectively-unknown source would start an agent run.
  const isPlatformSelected =
    platform !== IntegrationPlatform.UNRECOGNIZED &&
    platform !== IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM
  const canSave = isLegacyRepoSelected && isPlatformSelected

  // Memoized display data for legacy repos
  const legacyDisplayData = useMemo(() => {
    return [
      ...legacySelectedRepos.map(repo => ({
        id: `git-${repo.repository}-${repo.branch}`,
        name: extractFileDisplayName(repo.repository),
        branch: repo.branch || undefined
      })),
      ...legacyUploadedRepos.map(repo => ({
        id: `upload-${repo.id}`,
        name: repo.name,
        branch: undefined
      }))
    ]
  }, [legacySelectedRepos, legacyUploadedRepos])

  // Memoized display data for source repos
  const mulesoftDisplayData = useMemo(() => {
    return [
      ...mulesoftSelectedRepos.map(repo => ({
        id: `git-${repo.repository}-${repo.branch}`,
        name: extractFileDisplayName(repo.repository),
        branch: repo.branch || undefined
      })),
      ...mulesoftUploadedRepos.map(repo => ({
        id: `upload-${repo.id}`,
        name: repo.name,
        branch: undefined
      }))
    ]
  }, [mulesoftSelectedRepos, mulesoftUploadedRepos])

  // Memoized display data for metadata files
  const metadataDisplayData = useMemo(() => {
    return metadataUploadedFiles.map(file => ({
      id: `file-${file.id}`,
      name: file.name
    }))
  }, [metadataUploadedFiles])

  // Memoized platform items and dropdown width calculation.
  // ``getIntegrationPlatformItems`` filters out ``MULESOFT`` by default;
  // pass ``{ includeMuleSoft: true }`` to keep it in, since this screen
  // explicitly supports refactoring FROM Mule TO Spring Integration.
  // (Previously hand-pushed a Mulesoft entry behind a ``!items.some``
  // guard; the guard always passed today, but a future caller passing
  // ``includeMuleSoft: true`` upstream would have duplicated it.)
  const { platformItems, platformDropdownMinWidth } = useMemo(() => {
    const items = getIntegrationPlatformItems(
      [
        'UI_PATH_EXP_V1',
        'APIGEE_POLICY_V1',
        'NODE_JS',
        'SAP_PI_PO',
        'FLEX_GATEWAY_RUST_POLICY',
        // Exclude JAVA_SPRING_BOOT as the SOURCE platform — this is the
        // Spring Integration Refactor screen, which refactors INTO Spring
        // Boot. A user picking "Java Spring Boot" as source would trigger a
        // nonsensical "refactor Spring Boot into Spring Boot" agent run;
        // the backend forwards `platform` straight into
        // `MigrationDocumentInput.platform` with no validation, so the
        // safest gate is here at selection time.
        'JAVA_SPRING_BOOT'
      ],
      { includeMuleSoft: true }
    )
    // ``Math.max(...[])`` is ``-Infinity``, which would propagate to
    // ``minWidth = NaN`` and React would silently drop the inline
    // style — collapsing the dropdown to zero width with no console
    // error. Fall back to a sensible default (10 chars ≈ enough for
    // the typical platform label width) when ``items`` is empty.
    const longestLabelLength =
      items.length > 0 ? Math.max(...items.map(item => item.label.length)) : 10
    const minWidth = Math.ceil(longestLabelLength * 8 + 44)
    return { platformItems: items, platformDropdownMinWidth: minWidth }
  }, [])

  const handleSave = useCallback(() => {
    if (!canSave) return

    // Determine legacy option
    const legacyOption: ProjectSelectionOption =
      legacySelectedRepos.length > 0
        ? 'codeContext'
        : legacyUploadedRepos.length > 0
          ? 'upload'
          : 'none'

    const legacyS3Paths = legacyUploadedRepos
      .map(repo => repo.s3Path)
      .filter((path): path is string => Boolean(path))

    // Determine source (Mule) option
    const mulesoftOption: ProjectSelectionOption | undefined =
      mulesoftSelectedRepos.length > 0
        ? 'codeContext'
        : mulesoftUploadedRepos.length > 0
          ? 'upload'
          : undefined

    const mulesoftS3Paths = mulesoftUploadedRepos
      .map(repo => repo.s3Path)
      .filter((path): path is string => Boolean(path))

    const metadataS3Paths = metadataUploadedFiles
      .map(file => file.s3Path)
      .filter((path): path is string => Boolean(path))

    onSave({
      legacyRepos: legacySelectedRepos,
      legacyOption,
      legacyS3Paths,
      mulesoftRepos: mulesoftSelectedRepos,
      mulesoftOption,
      mulesoftS3Paths,
      platform,
      metadataS3Paths
    })
  }, [
    canSave,
    legacySelectedRepos,
    legacyUploadedRepos,
    mulesoftSelectedRepos,
    mulesoftUploadedRepos,
    metadataUploadedFiles,
    platform,
    onSave
  ])

  // While the task is being created on the server, stay on the repo-selection
  // step and show a loading spinner. We deliberately do NOT advance to "Select
  // Endpoints" or render the endpoint-identification view here — that task view
  // belongs to the conversation page and should only appear once the task_id
  // exists and the parent navigates there. This component stays mounted, so the
  // user's repo/platform selections are preserved if submission fails and we
  // fall back to the form.
  if (isSubmitting) {
    return (
      <SpringIntegrationRefactorFlowLayout
        currentStepNumber={0}
        proceedButtonText='Next: Select Endpoints'
        isProceedDisabled
        isProceedLoading
      >
        <div className='w-full h-full flex items-center justify-center'>
          <ProgressSpinner size='lg' />
        </div>
      </SpringIntegrationRefactorFlowLayout>
    )
  }

  return (
    <>
      <SpringIntegrationRefactorFlowLayout
        currentStepNumber={0}
        proceedButtonText='Next: Select Endpoints'
        isProceedDisabled={!canSave}
        onProceed={handleSave}
      >
        {/* Scrollable Container */}
        <div className='w-full h-full overflow-y-auto'>
          <div className={`flex justify-center w-full pb-20 ${isPlugin ? 'px-3' : 'px-[60px]'}`}>
            <div
              className={`task-form-body flex-grow w-full flex flex-col gap-12 ${isPlugin ? 'max-w-full min-w-0' : 'max-w-[720px] min-w-[600px]'}`}
            >
              {/* Modal Header */}
              <div className='pt-[60px] flex flex-col items-center gap-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-tokens-neutral-text-primary text-center'
                >
                  Select Source Repository
                </Typography>
              </div>

              {/* Platform Selection Card - Always visible at top */}
              <div className='rounded-[24px] px-6 py-4 flex flex-col gap-3 bg-tokens-neutral-fill-fill-1'>
                {/* Select source integration platform */}
                <div className='flex items-center justify-between w-full'>
                  <div className='flex items-center gap-4'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0 text-tokens-neutral-text-primary'
                    >
                      <path
                        d='M1.49805 3.75H7.49805V8.25H1.49805V3.75ZM2.99805 5.25V6.75H5.99805V5.25H2.99805ZM1.49805 9.75H16.498V14.25H1.49805V9.75ZM2.99805 11.25V12.75H14.998V11.25H2.99805Z'
                        fill='currentColor'
                      />
                    </svg>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-tokens-neutral-text-tertiary'
                      style={{ lineHeight: '22px' }}
                    >
                      Source Integration Platform
                    </Typography>
                  </div>
                  <div style={{ minWidth: platformDropdownMinWidth, maxWidth: 360 }}>
                    <Selector
                      placeholder='Select source platform'
                      value={integrationPlatformToJSON(platform)}
                      onSelectionChange={value => {
                        setPlatform(integrationPlatformFromJSON(value))
                      }}
                      className='w-full'
                      triggerClassName='px-2 py-1 bg-transparent border-none shadow-none h-auto min-h-0 rounded-lg hover:bg-tokens-neutral-fill-fill-4'
                      valueClassName='text-tokens-neutral-text-primary font-medium text-sm block !max-w-none !text-right'
                    >
                      {platformItems.map(item => (
                        <SelectItem key={item.key} value={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Selector>
                  </div>
                </div>

                {/* Divider */}
                <div className='h-px w-full bg-tokens-neutral-border-2' />

                {/* Legacy Platform Repository Section */}
                <div
                  className='group flex items-start gap-4 cursor-pointer py-1 transition-colors'
                  onClick={() => setLegacyRepoModalOpen(true)}
                >
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='shrink-0 mt-0.5 text-tokens-neutral-text-primary group-hover:text-tokens-status-link-3 transition-colors'
                  >
                    <path
                      d='M9 4.5V13.5M4.5 9H13.5'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <div className='flex flex-1 flex-col gap-1'>
                    {isLegacyRepoSelected ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Legacy platform repository (required)
                        </Typography>
                        {/* Display all repos in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {legacyDisplayData.map((repo, index) => (
                            <div key={repo.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='titleSm'
                                fontWeight='bold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {`Repository ${index + 1}: ${repo.name}`}
                              </Typography>
                              {repo.branch && (
                                <div className='flex items-center gap-2 mt-1'>
                                  <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 16 16'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='shrink-0 text-tokens-neutral-icon-solid'
                                  >
                                    <path
                                      d='M6 3V9C6 9.53043 6.21071 10.0391 6.58579 10.4142C6.96086 10.7893 7.46957 11 8 11H10'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <circle
                                      cx='12'
                                      cy='11'
                                      r='2'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                    <circle
                                      cx='6'
                                      cy='3'
                                      r='2'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='labelSm'
                                    className='text-tokens-neutral-text-tertiary'
                                    style={{ lineHeight: '14px' }}
                                  >
                                    {repo.branch}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Legacy platform repository (required)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          className='text-tokens-neutral-text-tertiary'
                          style={{ lineHeight: 'normal' }}
                        >
                          Connect your Git repository
                        </Typography>
                      </>
                    )}
                  </div>
                  {isLegacyRepoSelected && isPlatformSelected && <SelectedCheckmark />}
                </div>
              </div>

              {/* Additional Metadata Section - Separate Card */}
              <div
                className={`group border border-dashed border-tokens-neutral-border-2 hover:border-solid hover:border-tokens-neutral-border-2 rounded-[16px] cursor-pointer bg-tokens-neutral-fill-fill-1 ${metadataUploadedFiles.length > 0 ? 'min-h-[72px]' : 'h-[72px]'} flex items-center transition-all`}
                onClick={() => setMetadataModalOpen(true)}
              >
                <div className='flex items-start gap-4 p-4 w-full'>
                  <div className='flex items-center justify-center shrink-0 w-6 h-6 mt-0.5'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0 text-tokens-neutral-icon-solid'
                    >
                      <path
                        d='M3 4.5V9C3 9 3 11.25 8.25 11.25C13.5 11.25 13.5 9 13.5 9V4.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M8.25 2.25C13.5 2.25 13.5 4.5 13.5 4.5C13.5 4.5 13.5 6.75 8.25 6.75C3 6.75 3 4.5 3 4.5C3 4.5 3 2.25 8.25 2.25Z'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M8.25 15.75C3 15.75 3 13.5 3 13.5V9'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M11.25 15.75V14.25'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                      <path
                        d='M13.5 15.75V12.75'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                      <path
                        d='M15.75 15.75V11.25'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 flex flex-col gap-1'>
                    {metadataUploadedFiles.length > 0 ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Additional Metadata (optional)
                        </Typography>
                        {/* Display all files in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {metadataDisplayData.map(file => (
                            <div key={file.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                                style={{ lineHeight: '18px' }}
                              >
                                {file.name}
                              </Typography>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Additional Metadata (optional)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          className='text-tokens-neutral-text-tertiary'
                          style={{ lineHeight: 'normal' }}
                        >
                          Upload files containing information about the legacy repositories
                        </Typography>
                      </>
                    )}
                  </div>
                  {metadataUploadedFiles.length > 0 && <SelectedCheckmark />}
                </div>
              </div>

              {/* Source Code Section - Separate Card */}
              <div
                className={`group border border-dashed border-tokens-neutral-border-2 hover:border-solid hover:border-tokens-neutral-border-2 rounded-[16px] cursor-pointer bg-tokens-neutral-fill-fill-1 ${isMulesoftRepoSelected ? 'min-h-[72px]' : 'h-[72px]'} flex items-center transition-all`}
                onClick={() => setMulesoftRepoModalOpen(true)}
              >
                <div className='flex items-start gap-4 p-4 w-full'>
                  <div className='flex items-center justify-center shrink-0 w-6 h-6 mt-0.5'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0 text-tokens-neutral-text-primary group-hover:text-tokens-status-link-3 transition-colors'
                    >
                      <path
                        d='M9 4.5V13.5M4.5 9H13.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 flex flex-col gap-1'>
                    {isMulesoftRepoSelected ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Source Code (optional)
                        </Typography>
                        {/* Display all repos in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {mulesoftDisplayData.map((repo, index) => (
                            <div key={repo.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='titleSm'
                                fontWeight='bold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {`Repository ${index + 1}: ${repo.name}`}
                              </Typography>
                              {repo.branch && (
                                <div className='flex items-center gap-2 mt-1'>
                                  <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 16 16'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='shrink-0 text-tokens-neutral-icon-solid'
                                  >
                                    <path
                                      d='M6 3V9C6 9.53043 6.21071 10.0391 6.58579 10.4142C6.96086 10.7893 7.46957 11 8 11H10'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <circle
                                      cx='12'
                                      cy='11'
                                      r='2'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                    <circle
                                      cx='6'
                                      cy='3'
                                      r='2'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='labelSm'
                                    className='text-tokens-neutral-text-tertiary'
                                    style={{ lineHeight: '14px' }}
                                  >
                                    {repo.branch}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Source Code (optional)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          className='text-tokens-neutral-text-tertiary'
                          style={{ lineHeight: 'normal' }}
                        >
                          Add your source code as supplement reference
                        </Typography>
                      </>
                    )}
                  </div>
                  {isMulesoftRepoSelected && <SelectedCheckmark />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SpringIntegrationRefactorFlowLayout>

      {/* Legacy Repository Selection Modal */}
      <RepoSelectModal
        isOpen={legacyRepoModalOpen}
        onClose={() => setLegacyRepoModalOpen(false)}
        onConfirm={handleLegacyRepoConfirm}
        repositories={selectableRepos}
        getBranches={getBranches}
        title='Select Legacy Repository'
        initialSelectedRepos={legacySelectedRepos}
        initialUploadedRepos={legacyUploadedRepos}
        workspaceUuid={currentWorkspace?.uuid}
      />

      {/* Source Repository Selection Modal */}
      <RepoSelectModal
        isOpen={mulesoftRepoModalOpen}
        onClose={() => setMulesoftRepoModalOpen(false)}
        onConfirm={handleMulesoftRepoConfirm}
        repositories={selectableRepos}
        getBranches={getBranches}
        title='Select Source Reference Code'
        initialSelectedRepos={mulesoftSelectedRepos}
        initialUploadedRepos={mulesoftUploadedRepos}
        workspaceUuid={currentWorkspace?.uuid}
      />

      {/* Metadata Files Selection Modal */}
      <MetadataFileSelectModal
        isOpen={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        onConfirm={handleMetadataFilesConfirm}
        workspaceUuid={currentWorkspace?.uuid}
        initialFiles={metadataUploadedFiles}
      />
    </>
  )
}

export default SpringIntegrationRefactorRepoSelectionScreen
